import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { API_BASE, EMPLOYEE_API_TOKEN, EXCLUDED_USERS } from '../constants';
import { Loader, ErrorBanner } from './common';
import CustomTooltip from './common/CustomTooltip';
import ProgressIndicator from './time-tracking/ProgressIndicator';
import UserSelector from './time-tracking/UserSelector';
import ProjectDropdown from './time-tracking/ProjectDropdown';
import DateRangeSelector from './time-tracking/DateRangeSelector';
import FilterControls from './time-tracking/FilterControls';
import TaskBars from './time-tracking/TaskBars';
import TasksTable from './time-tracking/TasksTable';
import UserMetricsPanel from './time-tracking/UserMetricsPanel';

export default function WeeklyTimeTrackingWidget({ asana, userRole }) {
	// compute default week range (Mon–Sun)
	const today = new Date();
	const day = today.getDay();
	const startOfWeek = new Date(today);
	startOfWeek.setDate(today.getDate() - (day === 0 ? 6 : day - 1));

	// Ensure endOfWeek is not in the future, use either Sunday of current week or today
	const sundayOfWeek = new Date(startOfWeek);
	sundayOfWeek.setDate(startOfWeek.getDate() + 6);

	const endOfWeek = new Date(sundayOfWeek > today ? today : sundayOfWeek);

	// state
	const [users, setUsers] = useState([]);
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [projects, setProjects] = useState([]);
	const [selectedProjects, setSelectedProjects] = useState([]);
	const [startDate, setStartDate] = useState(startOfWeek);
	const [endDate, setEndDate] = useState(endOfWeek);
	const [tasks, setTasks] = useState([]);
	const [loading, setLoading] = useState(false); // Don't show loading initially
	const [loadingPhase, setLoadingPhase] = useState(''); // 'initial', 'projects', 'tasks', 'entries'
	const [error, setError] = useState(null);
	const [showNoEntries, setShowNoEntries] = useState(false);
	const [isDataStillUpdating, setIsDataStillUpdating] = useState(false);
	const [totalTasksToProcess, setTotalTasksToProcess] = useState(0);
	const [tasksProcessed, setTasksProcessed] = useState(0);
	// State to store parent task information
	const [parentTasks, setParentTasks] = useState({});
	// Add state for separate view mode - set default to true
	const [separateView, setSeparateView] = useState(true);
	// Add state for show details mode - set default to true
	const [showDetails, setShowDetails] = useState(true);
	// State to track if filters changed after last data load
	const [filtersChanged, setFiltersChanged] = useState(false);
	// State for Quick Range selector
	const [quickRangeValue, setQuickRangeValue] = useState('');
	// State for completed tasks filter - default to false (exclude completed tasks)
	const [onlyCompleted, setOnlyCompleted] = useState(false);
	// State for total hours loading
	const [loadingTotalHours, setLoadingTotalHours] = useState(false);
	// State for employee metrics (compensation-adjusted KPI)
	const [employeeMetrics, setEmployeeMetrics] = useState({});
	const [loadingEmployeeMetrics, setLoadingEmployeeMetrics] = useState(false);

	// refs
	const tteApiRef = useRef(asana.timeTrackingEntries || null);
	const workspaceGidRef = useRef(null);
	const fetchController = useRef(null);
	const tasksLoadingTimerRef = useRef(null);
	const dataUpdateTimerRef = useRef(null);
	const lastTaskUpdateRef = useRef(Date.now());
	// Ref to track initial task load status
	const tasksRef = useRef(tasks);
	tasksRef.current = tasks;

	// helper to handle auth error
	const handleAuthError = () => {
		localStorage.removeItem('asana_access');
		localStorage.removeItem('asana_refresh');
		window.location.reload();
	};

	// initialize TimeTrackingEntries API
	const ensureTTE = useCallback(async () => {
		if (tteApiRef.current?.getTimeTrackingEntriesForTask)
			return tteApiRef.current;
		try {
			const { TimeTrackingEntriesApi } = await import('asana');
			const api = new TimeTrackingEntriesApi(asana.client);
			tteApiRef.current = api;
			return api;
		} catch (e) {
			console.error('TimeTrackingEntriesApi init failed', e);
			return null;
		}
	}, [asana.client]);

	// parse Asana error to check for expired token
	const isTokenExpiredError = error => {
		try {
			const obj = typeof error === 'string' ? JSON.parse(error) : error;
			const msg = obj.errors?.[0]?.message || '';
			return msg.toLowerCase().includes('bearer token has expired');
		} catch {
			return false;
		}
	};

	// fetch users in workspace
	const fetchUsers = useCallback(async () => {
		// Only show loading if this is not the initial load
		setLoadingPhase('initial');
		setError(null);
		setShowNoEntries(false);
		setIsDataStillUpdating(false);
		setTotalTasksToProcess(0);
		setTasksProcessed(0);
		setParentTasks({}); // Reset parent tasks

		try {
			const { data: spaces } = await asana.workspaces.getWorkspaces();
			if (!spaces.length) throw new Error('No workspaces');
			workspaceGidRef.current = spaces[0].gid;

			const { data: members } = await asana.users.getUsersForWorkspace(
				spaces[0].gid,
				{ opt_fields: 'name' }
			);

			// Hide non-employees (clients, test accounts) from the selector
			const visibleMembers = members.filter(
				member => !EXCLUDED_USERS.includes(member.name)
			);
			setUsers(visibleMembers);

			// Don't automatically select first user anymore
			// setSelectedUsers([]); // Keep empty initially
		} catch (e) {
			console.error('fetchUsers()', e);
			const msg = e.message || e;
			if (isTokenExpiredError(msg)) return handleAuthError();
			setError('Failed to load users');
		} finally {
			setLoadingPhase('');
		}
	}, [asana.users, asana.workspaces]);

	// fetch projects in workspace with pagination support
	const fetchProjects = useCallback(async () => {
		if (!workspaceGidRef.current) return;

		setLoadingPhase('projects');
		try {
			let allProjects = [];
			let hasMore = true;
			let page = 1;
			let options = {
				opt_fields: 'name,archived,privacy_setting,members,members.name',
				limit: 100
			};

			// Load all projects with pagination
			while (hasMore) {
				setLoadingPhase(`projects (page ${page})`);

				const response = await asana.projects.getProjectsForWorkspace(
					workspaceGidRef.current,
					options
				);

				const data = response.data || [];
				allProjects = [...allProjects, ...data];

				// Check for pagination info
				const _response = response._response || {};
				const next_page = response.next_page || _response.next_page || _response.data?.next_page;

				if (next_page) {
					if (next_page.uri) {
						// Extract offset from URI
						const match = next_page.uri.match(/offset=([^&]+)/);
						if (match && match[1]) {
							options.offset = decodeURIComponent(match[1]);
							page++;
						} else {
							hasMore = false;
						}
					} else if (next_page.offset) {
						options.offset = next_page.offset;
						page++;
					} else {
						hasMore = false;
					}
				} else {
					hasMore = false;
				}

				// Safety check - if no new data, stop
				if (data.length === 0) {
					hasMore = false;
				}

				// Update UI with current progress
				setLoadingPhase(`projects (loaded ${allProjects.length} so far)`);
			}

			// Filter out archived projects
			const activeProjects = allProjects.filter(project => !project.archived);

			setProjects(activeProjects);
		} catch (e) {
			console.error('fetchProjects()', e);
			const msg = e.message || e;
			if (isTokenExpiredError(msg)) return handleAuthError();
			setError('Failed to load projects');
		} finally {
			setLoadingPhase('');
		}
	}, [asana.projects]);

	// Cleanup function for all timers and controllers
	const cleanupResources = useCallback(() => {
		if (fetchController.current) {
			fetchController.current.abort();
			fetchController.current = null;
		}

		if (tasksLoadingTimerRef.current) {
			clearTimeout(tasksLoadingTimerRef.current);
			tasksLoadingTimerRef.current = null;
		}

		if (dataUpdateTimerRef.current) {
			clearTimeout(dataUpdateTimerRef.current);
			dataUpdateTimerRef.current = null;
		}
	}, []);

	// Function to fetch parent task details
	const fetchParentTask = useCallback(
		async taskGid => {
			if (!taskGid) return null;

			try {
				const { data } = await asana.tasks.getTask(taskGid, {
					opt_fields: 'name,projects.name,parent',
				});
				return data;
			} catch (err) {
				console.warn(`Error fetching parent task for ${taskGid}:`, err);
				return null;
			}
		},
		[asana.tasks]
	);

	// Function to fetch total actual hours for tasks (extended period: -3 months)
	const fetchTotalHoursForTasks = useCallback(
		async (tasksWithTime, tteApi) => {
			if (!tasksWithTime || tasksWithTime.length === 0) return {};

			// Calculate extended date range (current period - 3 months)
			const extendedStartDate = new Date(startDate);
			extendedStartDate.setMonth(extendedStartDate.getMonth() - 3);
			extendedStartDate.setHours(0, 0, 0, 0);

			const extendedUntil = new Date(endDate);
			extendedUntil.setHours(23, 59, 59, 999);

			console.log(`Fetching total hours for extended period: ${extendedStartDate.toISOString()} - ${extendedUntil.toISOString()}`);

			// Create a map to store total hours by task-user combination
			const totalHoursMap = {};

			// Get unique original task GIDs
			const uniqueTaskGids = [...new Set(tasksWithTime.map(t => t.originalTaskGid))];

			// Process each unique task
			for (const taskGid of uniqueTaskGids) {
				try {
					// Fetch all time entries for this task
					const resp = await tteApi.getTimeTrackingEntriesForTask(
						taskGid,
						{
							opt_fields: 'created_at,duration_minutes,created_by',
						}
					);

					const entries = resp.data || [];

					// Filter entries by extended date range and selected users
					const filteredEntries = entries.filter(
						e =>
							selectedUsers.includes(e.created_by?.gid) &&
							new Date(e.created_at) >= extendedStartDate &&
							new Date(e.created_at) <= extendedUntil
					);

					// Group by user
					const byUser = {};
					filteredEntries.forEach(entry => {
						const userId = entry.created_by?.gid;
						if (!userId || !selectedUsers.includes(userId)) return;

						if (!byUser[userId]) {
							byUser[userId] = 0;
						}
						byUser[userId] += (entry.duration_minutes || 0) / 60;
					});

					// Store in map with task-user key
					Object.entries(byUser).forEach(([userId, hours]) => {
						const key = `${taskGid}-${userId}`;
						totalHoursMap[key] = hours;
					});
				} catch (err) {
					console.warn(`Failed to fetch total hours for task ${taskGid}:`, err);
				}
			}

			return totalHoursMap;
		},
		[selectedUsers, startDate, endDate]
	);

	// Function to fetch employee metrics for all employees
	const fetchEmployeeMetrics = useCallback(async () => {
		try {
			setLoadingEmployeeMetrics(true);

			const response = await fetch(`${API_BASE}/_employee_metrics.php`, {
				headers: {
					'X-Employee-Token': EMPLOYEE_API_TOKEN
				}
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();

			// API returns {department_averages: {...}, employees: {...}}
			// Employees object is already keyed by employee name
			const coefficientsMap = data.employees || {};

			setEmployeeMetrics(coefficientsMap);
			console.log('Employee metrics loaded:', Object.keys(coefficientsMap).length, 'employees');
		} catch (err) {
			console.warn('Failed to fetch employee metrics:', err);
			// Don't show error to user - employee metrics are optional feature
			setEmployeeMetrics({});
		} finally {
			setLoadingEmployeeMetrics(false);
		}
	}, []);

	// Improved task fetching with better cancellation, state management and pagination
	const fetchTasksByEntries = useCallback(
		async (forceRefresh = false) => {
			if (selectedUsers.length === 0 || !workspaceGidRef.current) return;

			// Clean up any existing resources
			cleanupResources();

			// Create a new controller for this request
			fetchController.current = new AbortController();
			const signal = fetchController.current.signal;

			// Used to track if this request gets cancelled
			let isCancelled = false;
			signal.addEventListener('abort', () => {
				isCancelled = true;
			});

			// Reset state and start loading
			setTasks([]);
			setParentTasks({});
			setLoading(true);
			setLoadingPhase('tasks');
			setError(null);
			setShowNoEntries(false);
			setIsDataStillUpdating(false);
			setLoadingTotalHours(false);
			setTotalTasksToProcess(0);
			setTasksProcessed(0);
			lastTaskUpdateRef.current = Date.now();

			try {
				const since = new Date(startDate);
				since.setHours(0, 0, 0, 0);
				const until = new Date(endDate);
				until.setHours(23, 59, 59, 999);

				// Get time tracking API
				const tteApi = await ensureTTE();
				if (isCancelled) return;

				// Process for each selected user
				let allTasksFromAllUsers = [];

				for (const userId of selectedUsers) {
					// First fetch all tasks with pagination support
					setLoadingPhase(`tasks for ${users.find(u => u.gid === userId)?.name || 'user'}`);

					let userTasks = [];

					// Always fetch all tasks for user first, then filter by projects if needed
					// This ensures we don't miss subtasks that might be in different projects
					let allTasks = [];
					let hasMore = true;
					let page = 1;
					let options = {
						workspace: workspaceGidRef.current,
						assignee: userId,
						completed_since: since.toISOString(),
						modified_since: since.toISOString(),
						opt_fields: 'gid,name,projects.name,parent,assignee,completed,custom_fields.name,custom_fields.number_value,custom_fields.text_value,custom_fields.enum_value.name',
						limit: 100,
						include_archived: true,
					};

					while (hasMore && !isCancelled) {
						try {
							setLoadingPhase(`tasks (page ${page})`);

							const response = await asana.tasks.getTasks(options);

							// Extract data from response
							const data = response.data || [];

							// Add tasks to our collection
							allTasks = [...allTasks, ...data];

							// Check for pagination info in several possible places
							const _response = response._response || {};
							const next_page =
								response.next_page ||
								_response.next_page ||
								_response.data?.next_page;

							if (next_page) {
								if (next_page.uri) {
									// If we have a URI, extract the offset param from it
									const uri = next_page.uri;

									const match = uri.match(/offset=([^&]+)/);
									if (match && match[1]) {
										options.offset = decodeURIComponent(match[1]);
										page++;

										// Update UI after each page
										setLoadingPhase(`tasks (loaded ${allTasks.length} so far)`);
									} else {
										hasMore = false;
									}
								} else if (next_page.offset) {
									// If we have an offset directly
									options.offset = next_page.offset;
									page++;

									// Update UI after each page
									setLoadingPhase(`tasks (loaded ${allTasks.length} so far)`);
								} else {
									hasMore = false;
								}
							} else {
								hasMore = false;
							}

							// Safety check - if we didn't get any new tasks, stop
							if (data.length === 0) {
								hasMore = false;
							}
						} catch (error) {
							console.error('Error during pagination:', error);
							hasMore = false;
						}
					}

					userTasks = allTasks;

					// Filter completed
					if (onlyCompleted) {
						userTasks = userTasks.filter(task => task.completed);
					}

					// Remove duplicates based on task gid (in case task appears in multiple projects)
					const uniqueUserTasks = userTasks.filter((task, index, self) =>
						index === self.findIndex(t => t.gid === task.gid)
					);

					// Add to all tasks
					allTasksFromAllUsers = [...allTasksFromAllUsers, ...uniqueUserTasks];
				}

				// Load parent tasks information for proper project filtering
				// Collect all unique parent task IDs
				const parentTaskGidsForFiltering = new Set();
				allTasksFromAllUsers.forEach(task => {
					if (task.parent && task.parent.gid) {
						parentTaskGidsForFiltering.add(task.parent.gid);
					}
				});

				// Load parent tasks data if we have any
				if (parentTaskGidsForFiltering.size > 0) {
					setLoadingPhase(`loading parent tasks (${parentTaskGidsForFiltering.size})`);

					const parentTasksData = [];
					const parentGidsArray = Array.from(parentTaskGidsForFiltering);

					// Load parent tasks in batches
					const parentBatchSize = 10;
					for (let i = 0; i < parentGidsArray.length; i += parentBatchSize) {
						if (isCancelled) return;

						const batch = parentGidsArray.slice(i, i + parentBatchSize);
						const batchPromises = batch.map(async (gid) => {
							try {
								const { data } = await asana.tasks.getTask(gid, {
									opt_fields: 'gid,name,projects.name,parent,assignee'
								});
								return data;
							} catch (error) {
								console.warn(`Failed to load parent task ${gid}:`, error);
								return null;
							}
						});

						const batchResults = await Promise.all(batchPromises);
						const validResults = batchResults.filter(task => task !== null);
						parentTasksData.push(...validResults);
					}

					// Add parent tasks to our task collection for filtering (but don't include them in final results unless they have time entries)
					// IMPORTANT: Only add parent tasks that are NOT already in allTasksFromAllUsers to avoid overwriting tasks with custom_fields
					const existingTaskGids = new Set(allTasksFromAllUsers.map(t => t.gid));
					const newParentTasks = parentTasksData.filter(pt => !existingTaskGids.has(pt.gid));
					allTasksFromAllUsers = [...allTasksFromAllUsers, ...newParentTasks];
				}				// Apply project filtering after loading all tasks
				let filteredTasks = allTasksFromAllUsers;
				if (selectedProjects.length > 0) {
					setLoadingPhase('filtering by projects');

					// Create a set of selected project IDs for faster lookup
					const selectedProjectSet = new Set(selectedProjects);

					// Function to check if task belongs to selected projects
					const isTaskInSelectedProjects = (task) => {
						if (!task.projects || task.projects.length === 0) return false;
						return task.projects.some(project => selectedProjectSet.has(project.gid));
					};

					// Build a map of all tasks by their gid for quick lookup
					const taskMap = new Map();
					allTasksFromAllUsers.forEach(task => {
						taskMap.set(task.gid, task);
					});

					// Set to track tasks that should be included
					const includedTaskIds = new Set();

					// First pass: find all tasks that directly belong to selected projects
					allTasksFromAllUsers.forEach(task => {
						if (isTaskInSelectedProjects(task)) {
							includedTaskIds.add(task.gid);

							// If this task has a parent, include the parent too for context
							if (task.parent && task.parent.gid) {
								includedTaskIds.add(task.parent.gid);
							}
						}
					});

					// Second pass: for each included task, also include its subtasks
					const addSubtasks = (taskGid) => {
						allTasksFromAllUsers.forEach(task => {
							if (task.parent && task.parent.gid === taskGid) {
								if (!includedTaskIds.has(task.gid)) {
									includedTaskIds.add(task.gid);
									// Recursively add subtasks of this subtask
									addSubtasks(task.gid);
								}
							}
						});
					};

					// Add subtasks for all currently included tasks
					const currentlyIncluded = Array.from(includedTaskIds);
					currentlyIncluded.forEach(taskGid => {
						addSubtasks(taskGid);
					});

					// Enhanced logic: also include subtasks if their parent belongs to selected projects
					// This handles the case where parent task is in the selected project but subtask might be in different project
					allTasksFromAllUsers.forEach(task => {
						if (task.parent && task.parent.gid && !includedTaskIds.has(task.gid)) {
							// Check if the parent task belongs to selected projects
							const parentTask = taskMap.get(task.parent.gid);
							if (parentTask && isTaskInSelectedProjects(parentTask)) {
								includedTaskIds.add(task.gid);
								includedTaskIds.add(task.parent.gid); // Ensure parent is also included
							}
						}
					});

					// Filter to only include selected tasks
					filteredTasks = allTasksFromAllUsers.filter(task =>
						includedTaskIds.has(task.gid)
					);

					console.log(`Tasks after project filtering: ${filteredTasks.length} (from ${allTasksFromAllUsers.length})`);
				}				// Now we need to filter out parent tasks that were only loaded for filtering purposes
				// Keep only tasks that were originally assigned to selected users
				const originalUserTaskGids = new Set();
				for (const userId of selectedUsers) {
					allTasksFromAllUsers.forEach(task => {
						if (task.assignee && task.assignee.gid === userId) {
							originalUserTaskGids.add(task.gid);
						}
					});
				}

				// Final filtered tasks should only include originally assigned tasks
				filteredTasks = filteredTasks.filter(task =>
					originalUserTaskGids.has(task.gid)
				);

				// Use filteredTasks instead of allTasksFromAllUsers
				const rawTasks = filteredTasks;

				if (isCancelled) return;

				if (rawTasks.length === 0) {
					setLoading(false);
					setShowNoEntries(true);
					return;
				}

				// Collect parent task information
				const newParentTasks = {};
				const parentTasksToFetch = new Set();

				// Identify which tasks need parent information
				rawTasks.forEach(task => {
					if (task.parent && task.parent.gid) {
						parentTasksToFetch.add(task.parent.gid);
					}
				});

				// Fetch parent tasks info in batches
				const parentTaskBatchSize = 5;
				const parentTaskGids = Array.from(parentTasksToFetch);

				for (let i = 0; i < parentTaskGids.length; i += parentTaskBatchSize) {
					if (isCancelled) return;

					const batch = parentTaskGids.slice(i, i + parentTaskBatchSize);
					const parentBatchResults = await Promise.all(
						batch.map(gid => fetchParentTask(gid))
					);

					parentBatchResults.forEach(parentTask => {
						if (parentTask) {
							newParentTasks[parentTask.gid] = parentTask;
						}
					});
				}

				setParentTasks(newParentTasks);

				setTotalTasksToProcess(rawTasks.length);

				// Cache to keep track of which tasks we've already loaded
				const processedTaskIds = new Set();

				// Now process tasks in batches to avoid too many concurrent requests
				setLoadingPhase('entries');
				const batchSize = 8; // Larger batch size for fewer API calls
				let processedTasks = [];
				let tasksWithTime = [];
				let foundAnyEntries = false;

				// Improve the time entry processing function to assign entries to specific users
				const getEntriesForTask = async task => {
					if (processedTaskIds.has(task.gid)) return null; // Skip if already processed

					let entries = [];
					try {
						if (tteApi) {
							const resp = await tteApi.getTimeTrackingEntriesForTask(
								task.gid,
								{
									opt_fields: 'created_at,duration_minutes,created_by',
								}
							);
							entries = resp.data || [];
						}
					} catch (e) {
						console.warn(`No entries for task ${task.gid}`, e);
					}

					setTasksProcessed(prev => {
						return Math.min(prev + 1, rawTasks.length);
					});

					processedTaskIds.add(task.gid); // Mark as processed regardless of result

					// Filter entries by selected users and date range
					const filteredEntries = entries.filter(
						e =>
							selectedUsers.includes(e.created_by?.gid) &&
							new Date(e.created_at) >= since &&
							new Date(e.created_at) <= until
					);

					// If we have no entries from selected users, skip this task
					if (filteredEntries.length === 0) return null;

					// Create separate task entries for each user
					const tasksByUser = {};

					// Group the time entries by user
					filteredEntries.forEach(entry => {
						const userId = entry.created_by?.gid;
						if (!userId || !selectedUsers.includes(userId)) return;

						if (!tasksByUser[userId]) {
							tasksByUser[userId] = {
								minutes: 0,
								user: users.find(u => u.gid === userId)
							};
						}

						tasksByUser[userId].minutes += entry.duration_minutes || 0;
					});

					// Create task objects for each user
					const enhancedTasks = [];
					Object.entries(tasksByUser).forEach(([userId, data]) => {
						// Only create task entries for users with actual time
						if (data.minutes > 0) {
							foundAnyEntries = true;
							lastTaskUpdateRef.current = Date.now();

							// Extract custom fields for estimated hours and story points
							const extractCustomFields = (task) => {
								const customFields = {};

								if (task.custom_fields && Array.isArray(task.custom_fields)) {
									task.custom_fields.forEach(field => {
										const fieldName = field.name?.toLowerCase();

										// Look for estimated hours field variations
										if (fieldName && (
											fieldName.includes('estimated')
										) && (fieldName.includes('hour') || fieldName.includes('time'))) {
											// Convert minutes to hours if the value is reasonable for minutes (> 24)
											const value = field.number_value || 0;
											customFields.estimatedHours = value > 24 ? value / 60 : value;
										}

										if (fieldName && fieldName === 'story points') {
											customFields.storyPoints = field.number_value || 0;
										}

										if (fieldName && fieldName === 'qa points') {
											customFields.qaPoints = field.number_value || 0;
										}

										// Reopened Issues — enum field; "Reopened" option marks redone work
										if (fieldName && fieldName === 'reopened issues') {
											customFields.reopened = (field.enum_value?.name || '').toLowerCase() === 'reopened';
										}

											if (fieldName && fieldName === 'design points') {
												customFields.designPoints = field.number_value || 0;
											}
									});
								}
								return customFields;
							};

							const customFields = extractCustomFields(task);

							// Enhanced task data with parent info and custom fields
							enhancedTasks.push({
								...task,
								gid: `${task.gid}-${userId}`, // Create unique ID for each user's task
								projectNames: task.projects?.length
									? task.projects.map(p => p.name).join(', ')
									: '',
								actualHours: data.minutes / 60,
								parentGid: task.parent?.gid || null,
								isSubtask: !!task.parent,
								userName: data.user ? data.user.name : 'Unknown user',
								originalTaskGid: task.gid, // Keep the original task ID
								...customFields
							});
						}
					});

					// If we have multiple task entries (one per user), return them all
					return enhancedTasks.length > 0 ? enhancedTasks : null;
				};

				// Function to update tasks state and track data still updating
				const updateTasksState = newTasks => {
					setTasks(newTasks);
					setIsDataStillUpdating(true);
					lastTaskUpdateRef.current = Date.now();

					// Clear existing timer
					if (dataUpdateTimerRef.current) {
						clearTimeout(dataUpdateTimerRef.current);
					}

					// Set a timer to turn off the "updating" indicator after no updates for 1.5 seconds
					dataUpdateTimerRef.current = setTimeout(() => {
						const timeSinceUpdate = Date.now() - lastTaskUpdateRef.current;
						if (timeSinceUpdate >= 1500) {
							setIsDataStillUpdating(false);
						}
					}, 1800);
				};

				// If we have many tasks, first try to find a sample to quickly show results
				if (rawTasks.length > 20) {
					// Pick 10 random tasks from the list to try first
					const sampleTasks = [];
					const tasksCopy = [...rawTasks];
					for (let i = 0; i < Math.min(10, tasksCopy.length); i++) {
						const randomIndex = Math.floor(Math.random() * tasksCopy.length);
						sampleTasks.push(tasksCopy.splice(randomIndex, 1)[0]);
					}

					// Process this sample batch with high priority
					const sampleResults = await Promise.all(
						sampleTasks.map(getEntriesForTask)
					);

					// Ensure proper flattening of results
					const validSampleTasks = [];
					sampleResults.forEach(result => {
						if (!result) return;
						if (Array.isArray(result)) {
							validSampleTasks.push(...result);
						} else {
							validSampleTasks.push(result);
						}
					});

					if (validSampleTasks.length > 0) {
						// Ensure all tasks are objects, not arrays
						const cleanTasks = validSampleTasks.filter(
							task => typeof task === 'object' && !Array.isArray(task) && task !== null
						);

						// Update UI immediately with first findings
						tasksWithTime = cleanTasks.sort(
							(a, b) => (b.actualHours || 0) - (a.actualHours || 0)
						);
						updateTasksState(tasksWithTime);
						processedTasks = [...cleanTasks];
					}
				}

				// Set a timer to show "no entries" message if we've been loading too long without results
				// BUT make sure it doesn't show while we have actual results
				tasksLoadingTimerRef.current = setTimeout(() => {
					if (
						!foundAnyEntries &&
						!isCancelled &&
						tasksRef.current.length === 0
					) {
						setShowNoEntries(true);
					}
				}, 15000); // Increased timeout to prevent flickering

				// Process all tasks in batches
				for (let i = 0; i < rawTasks.length; i += batchSize) {
					if (isCancelled) return;

					const batch = rawTasks.slice(i, i + batchSize);
					const remainingTasks = batch.filter(
						task => !processedTaskIds.has(task.gid)
					);

					if (remainingTasks.length === 0) continue; // Skip if all tasks in this batch are already processed

					const currentMin = Math.min(i + 1, rawTasks.length);
					const currentMax = Math.min(i + batchSize, rawTasks.length);

					setLoadingPhase(
						`entries (${currentMin}-${currentMax} of ${rawTasks.length})`
					);

					const batchResults = await Promise.all(
						remainingTasks.map(getEntriesForTask)
					);

					if (isCancelled) return;

					// Modify the function that processes batches of tasks to correctly handle our data structure
					const validResults = [];
					batchResults.forEach(result => {
						if (!result) return; // Skip null results

						if (Array.isArray(result)) {
							// If it's an array, add each item
							validResults.push(...result);
						} else {
							// If it's a single object, add it directly
							validResults.push(result);
						}
					});

					if (validResults.length > 0) {
						// We found entries, hide the "no entries" message if it was shown
						setShowNoEntries(false);

						// Add this batch to processed tasks
						processedTasks = [...processedTasks, ...validResults];

						// Update UI with tasks that have time entries
						tasksWithTime = processedTasks.sort(
							(a, b) => (b.actualHours || 0) - (a.actualHours || 0)
						);

						updateTasksState(tasksWithTime);
					}
				}

				// Clean up timers
				if (tasksLoadingTimerRef.current) {
					clearTimeout(tasksLoadingTimerRef.current);
					tasksLoadingTimerRef.current = null;
				}

				// Final state updates
				if (tasksWithTime.length === 0 && !isCancelled) {
					setShowNoEntries(true);
				}

				// Fetch total hours for all tasks (extended period: -2 months)
				if (tasksWithTime.length > 0 && !isCancelled) {
					setLoadingPhase('total hours');
					setLoadingTotalHours(true);

					try {
						const totalHoursMap = await fetchTotalHoursForTasks(tasksWithTime, tteApi);

						// Add totalActualHours to each task
						const tasksWithTotalHours = tasksWithTime.map(task => {
							const key = task.gid; // task.gid already has format taskGid-userId
							const totalHours = totalHoursMap[key] || task.actualHours;

							return {
								...task,
								totalActualHours: totalHours,
							};
						});

						// Update state with total hours
						setTasks(tasksWithTotalHours);
						setLoadingTotalHours(false);

						console.log(`Added total hours to ${tasksWithTotalHours.length} tasks`);
					} catch (err) {
						console.error('Failed to fetch total hours:', err);
						setLoadingTotalHours(false);
						// Continue without total hours - will use actualHours as fallback
					}
				}

				setLoading(false);

				// Set a final timer to turn off the "updating" indicator
				if (dataUpdateTimerRef.current) {
					clearTimeout(dataUpdateTimerRef.current);
				}
				dataUpdateTimerRef.current = setTimeout(() => {
					setIsDataStillUpdating(false);
				}, 1500);
			} catch (e) {
				if (isCancelled) return;

				console.error('fetchTasksByEntries()', e);
				const msg = e.message || e;
				if (isTokenExpiredError(msg)) return handleAuthError();
				setError('Failed to load tasks');
				setLoading(false);
				setIsDataStillUpdating(false);
			}
		},
		[
			asana.tasks,
			ensureTTE,
			selectedUsers,
			selectedProjects,
			startDate,
			endDate,
			onlyCompleted,
			cleanupResources,
			fetchParentTask,
			fetchTotalHoursForTasks,
			users,
		]
	); // eslint-disable-line react-hooks/exhaustive-deps

	// Cancel any ongoing fetch when component unmounts or user changes
	useEffect(() => {
		return () => {
			cleanupResources();
		};
	}, [selectedUsers, cleanupResources]);

	// Fetch employee metrics on component mount
	useEffect(() => {
		fetchEmployeeMetrics();
	}, [fetchEmployeeMetrics]);

	// Toggle handler for view mode
	const handleToggleView = () => {
		setSeparateView(prev => !prev);
	};

	// Toggle handler for show details mode
	const handleToggleShowDetails = () => {
		setShowDetails(prev => !prev);
	};

	// Toggle completed tasks filter
	const handleToggleOnlyCompleted = () => {
		setOnlyCompleted(prev => !prev);
		setFiltersChanged(true);
	};

	// User selection handler - modified for multi/single mode
	const handleUserSelect = userId => {
		setSelectedUsers(prevSelectedUsers => {
			// Toggle the user in/out of the multi-selection (last one can be removed too)
			if (prevSelectedUsers.includes(userId)) {
				return prevSelectedUsers.filter(id => id !== userId);
			}
			return [...prevSelectedUsers, userId];
		});

		// Ensure we don't show "no entries" when switching users
		setShowNoEntries(false);
		setFiltersChanged(true); // Отмечаем что фильтры изменились

		// Cancel any ongoing requests when selection changes
		cleanupResources();
	};

	// Remove a single user from the selection (pill ✕)
	const handleRemoveUser = userId => {
		setSelectedUsers(prevSelectedUsers => prevSelectedUsers.filter(id => id !== userId));
		setShowNoEntries(false);
		setFiltersChanged(true);
		cleanupResources();
	};

	// Clear the entire user selection (pills "Clear all")
	const handleClearUsers = () => {
		setSelectedUsers([]);
		setShowNoEntries(false);
		setFiltersChanged(true);
		cleanupResources();
	};

	// Project selection handler - always in multi mode
	const handleProjectSelect = (projectId, projectIds = null) => {
		// Handle special cases for "ALL" and "NONE"
		if (projectId === 'ALL') {
			// "All projects" means no specific filtering - clear selection
			setSelectedProjects([]);
			setFiltersChanged(true); // Отмечаем что фильтры изменились
			return;
		}
		if (projectId === 'NONE') {
			setSelectedProjects([]);
			setFiltersChanged(true); // Отмечаем что фильтры изменились
			return;
		}

		setSelectedProjects(prevSelectedProjects => {
			// Toggle the selection in multi-mode
			if (prevSelectedProjects.includes(projectId)) {
				return prevSelectedProjects.filter(id => id !== projectId);
			} else {
				return [...prevSelectedProjects, projectId];
			}
		});
		setFiltersChanged(true); // Отмечаем что фильтры изменились
	};

	// Add handler to reset all filters
	const handleResetFilters = () => {
		// Cancel any ongoing requests
		cleanupResources();

		setSelectedUsers([]);
		setSelectedProjects([]);
		setTasks([]);
		setParentTasks({});
		setShowNoEntries(false);
		setQuickRangeValue('');
		setOnlyCompleted(false);
		setFiltersChanged(false);

		// Reset all loading states
		setLoading(false);
		setLoadingPhase('');
		setIsDataStillUpdating(false);
		setLoadingTotalHours(false);
		setTotalTasksToProcess(0);
		setTasksProcessed(0);

		// Reset to default date range
		const today = new Date();
		const day = today.getDay();
		const startOfWeek = new Date(today);
		startOfWeek.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
		const sundayOfWeek = new Date(startOfWeek);
		sundayOfWeek.setDate(startOfWeek.getDate() + 6);
		const endOfWeek = new Date(sundayOfWeek > today ? today : sundayOfWeek);

		setStartDate(startOfWeek);
		setEndDate(endOfWeek);
	};

	// Manual load data handler
	const handleLoadData = () => {
		if (selectedUsers.length === 0) return;

		setFiltersChanged(false);
		fetchTasksByEntries(true);
	};

	// Date change handlers
	const handleStartDateChange = e => {
		setStartDate(new Date(e.target.value));
		setQuickRangeValue('');
		setFiltersChanged(true);
	};

	const handleEndDateChange = e => {
		setEndDate(new Date(e.target.value));
		setQuickRangeValue('');
		setFiltersChanged(true);
	};

	const toNoon = d => {
		d.setHours(12, 0, 0, 0);
		return d;
	};

	function handleQuickRangeSelect(e) {
		const value = e.target.value;
		setQuickRangeValue(value);

		if (!value) return;

		const today = new Date();
		today.setHours(0, 0, 0, 0);

		let start, end;

		switch (value) {
			case 'current_week': {
				const dow = today.getDay();
				const diffToMon = dow === 0 ? -6 : 1 - dow;
				start = new Date(today);
				start.setDate(today.getDate() + diffToMon);
				end = new Date(today);
				break;
			}

			case 'previous_week': {
				const dow = today.getDay();
				const diffToMon = dow === 0 ? -6 : 1 - dow;
				const mondayThisWeek = new Date(today);
				mondayThisWeek.setDate(today.getDate() + diffToMon);

				start = new Date(mondayThisWeek);
				start.setDate(mondayThisWeek.getDate() - 7);
				end = new Date(start);
				end.setDate(start.getDate() + 6);
				break;
			}

			case 'current_month':
				start = new Date(today.getFullYear(), today.getMonth(), 1);
				end = new Date(today);
				break;

			case 'previous_month':
				start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
				end = new Date(today.getFullYear(), today.getMonth(), 0);
				break;

			case 'current_year':
				start = new Date(today.getFullYear(), 0, 1); // January 1st of current year
				end = new Date(today);
				break;

			default: {
				const weeks = parseInt(value, 10);
				if (!Number.isFinite(weeks) || weeks <= 0) return;

				start = new Date(today);
				start.setDate(today.getDate() - weeks * 7 + 1);
				end = new Date(today);
			}
		}

		start = toNoon(start);
		end = toNoon(end);

		console.log(
			`Range selected: ${start.toISOString().slice(0, 10)} to ${end
				.toISOString()
				.slice(0, 10)}`
		);

		setStartDate(start);
		setEndDate(end);
		setFiltersChanged(true); // Отмечаем что фильтры изменились
	}

	// Initialize users and projects
	useEffect(() => {
		const initializeData = async () => {
			await fetchUsers();
			await fetchProjects();
		};

		initializeData();

		// Cleanup when component unmounts
		return () => {
			cleanupResources();
		};
	}, [fetchUsers, fetchProjects, cleanupResources]);

	// Remove automatic data loading - now users must click "Load Data" button
	// useEffect(() => {
	// 	if (selectedUsers.length) {
	// 		fetchTasksByEntries();
	// 	}
	// }, [fetchTasksByEntries, selectedUsers]);

	// Update the totalHours calculation to handle arrays and undefined values
	const totalHours = tasks.reduce((sum, t) => {
		// Skip if t is an array or actualHours is undefined
		if (Array.isArray(t) || typeof t !== 'object' || t === null || typeof t.actualHours !== 'number') {
			return sum;
		}
		return sum + t.actualHours;
	}, 0);

	// Loading state message based on current phase
	const getLoadingMessage = () => {
		switch (loadingPhase) {
			case 'initial':
				return 'Loading users...';
			case 'tasks':
				return 'Loading tasks...';
			default:
				if (loadingPhase.startsWith('entries')) {
					return loadingPhase.replace(
						'entries',
						'Loading time entries for tasks'
					);
				}
				return 'Loading...';
		}
	};

	// Get selected user names for display
	const getSelectedUserNames = () => {
		return selectedUsers.map(userId => {
			const user = users.find(u => u.gid === userId);
			return user ? user.name : 'Unknown user';
		});
	};

	// Content rendering based on state
	const renderContent = () => {
		// Show tasks if we have some, regardless of loading state
		if (tasks.length > 0) {
			return (
				<>
					<TasksTable
						tasks={tasks}
						parentTasks={parentTasks}
						totalHours={totalHours}
						selectedUserNames={getSelectedUserNames()}
						multiMode={selectedUsers.length > 1}
						separateView={separateView}
						showDetails={showDetails}
						startDate={startDate}
						endDate={endDate}
						isDataStillUpdating={isDataStillUpdating}
						loading={loading}
						tasksProcessed={tasksProcessed}
						totalTasksToProcess={totalTasksToProcess}
						userRole={userRole}
						employeeMetrics={employeeMetrics}
					/>
					<UserMetricsPanel
						tasks={tasks}
						selectedUserNames={getSelectedUserNames()}
						multiMode={selectedUsers.length > 1}
						separateView={separateView}
						loadingTotalHours={loadingTotalHours}
						employeeMetrics={employeeMetrics}
						loadingEmployeeMetrics={loadingEmployeeMetrics}
						userRole={userRole}
					/>
					<TaskBars tasks={tasks} totalHours={totalHours} />
					{(loading || isDataStillUpdating) && (
						<div className='mt-4 text-center text-sm text-blue-500 animate-pulse'>
							{getLoadingMessage()}
						</div>
					)}
				</>
			);
		}

		// Show loading message and hide "no entries" during initial load
		if (loading && !showNoEntries) {
			return (
				<>
					<Loader message={getLoadingMessage()} />
					<ProgressIndicator
						tasksProcessed={tasksProcessed}
						totalTasksToProcess={totalTasksToProcess}
					/>
				</>
			);
		}

		// Show "no entries" message when appropriate
		if (showNoEntries) {
			return (
				<div className='text-center p-4'>
					<p className='text-sm text-gray-500'>
						No time entries found for selected user in this date range.
					</p>
					{loading && (
						<>
							<div className='mt-4 w-full flex justify-center'>
								<div className='w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin'></div>
							</div>
							<p className='mt-3 text-xs text-blue-500 animate-pulse'>
								{getLoadingMessage()}
							</p>
							<ProgressIndicator
								tasksProcessed={tasksProcessed}
								totalTasksToProcess={totalTasksToProcess}
							/>
						</>
					)}
				</div>
			);
		}

		// Default state - no loading, no data, no message
		// Show instructions or empty state
		return (
			<div className='text-center p-4'>
				<p className='text-sm text-gray-500'>
					Select users and click "Load Data" to view time tracking information.
				</p>
			</div>
		);
	};

	if (error) return <ErrorBanner message={error} />;

	return (
		<div className='p-4 bg-white rounded-md shadow-md text-gray-900'>
			<CustomTooltip />
			<UserSelector
				users={users}
				selectedUsers={selectedUsers}
				loading={loading}
				isDataStillUpdating={isDataStillUpdating}
				onUserSelect={handleUserSelect}
				onClearUsers={handleClearUsers}
				onRemoveUser={handleRemoveUser}
				separateView={separateView}
				onToggleView={handleToggleView}
				showDetails={showDetails}
				onToggleShowDetails={handleToggleShowDetails}
				userRole={userRole}
				employeeMetrics={employeeMetrics}
			/>
			<label className='block mt-6 mb-2 font-medium'>Filters and Period</label>
			<div className='flex flex-wrap gap-4 mb-4'>
				<div className='flex-shrink-0 w-64'>
					<ProjectDropdown
						projects={projects}
						selectedProjects={selectedProjects}
						loading={loading}
						isDataStillUpdating={isDataStillUpdating}
						onProjectSelect={handleProjectSelect}
					/>
				</div>
				<div className='flex-1 min-w-0'>
					<DateRangeSelector
						startDate={startDate}
						endDate={endDate}
						loading={loading}
						isDataStillUpdating={isDataStillUpdating}
						quickRangeValue={quickRangeValue}
						onStartDateChange={handleStartDateChange}
						onEndDateChange={handleEndDateChange}
						onQuickRangeSelect={handleQuickRangeSelect}
						onlyCompleted={onlyCompleted}
						onToggleOnlyCompleted={handleToggleOnlyCompleted}
					/>
				</div>
			</div>
			<FilterControls
				hasSelectedUsers={selectedUsers.length > 0}
				hasSelectedProjects={selectedProjects.length > 0}
				loading={loading}
				isDataStillUpdating={isDataStillUpdating}
				onLoadData={handleLoadData}
				onResetFilters={handleResetFilters}
				hasLoadedData={tasks.length > 0}
				filtersChanged={filtersChanged}
			/>
			{renderContent()}
		</div>
	);
}

WeeklyTimeTrackingWidget.propTypes = {
	asana: PropTypes.shape({
		client: PropTypes.object.isRequired,
		workspaces: PropTypes.object.isRequired,
		users: PropTypes.object.isRequired,
		tasks: PropTypes.object.isRequired,
		projects: PropTypes.object.isRequired,
		timeTrackingEntries: PropTypes.object,
	}).isRequired,
	userRole: PropTypes.string,
};
